import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, X } from "lucide-react";
import { showInfo } from "@/lib/toast";

interface InviteCodeDialogProps {
  open: boolean;
  onConfirm: (inviteCode: string) => void;
  onCancel: () => void;
}

export function InviteCodeDialog({
  open,
  onConfirm,
  onCancel,
}: InviteCodeDialogProps) {
  const [inviteCode, setInviteCode] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    setInviteCode(filtered);
  };

  const handleConfirm = () => {
    const trimmed = inviteCode.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
    setInviteCode("");
  };

  const handleCancel = () => {
    setInviteCode("");
    onCancel();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{
            background: "rgba(3,7,18,0.82)",
            backdropFilter: "blur(8px)",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative w-full max-w-[392px] overflow-hidden rounded-[14px] px-5 pb-6 pt-4"
            style={{
              background: "linear-gradient(180deg, #0c1018 0%, #0b0e15 100%)",
              border: "1px solid rgba(49,78,72,0.72)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.58)",
            }}
          >
            <div
              className="absolute inset-x-0 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, rgba(0,0,0,0), rgba(43,214,184,0.5), rgba(0,0,0,0))",
              }}
            />

            <div className="mb-7 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-full"
                  style={{ background: "rgba(19,214,177,0.08)" }}
                >
                  <Users
                    size={14}
                    strokeWidth={2.2}
                    style={{ color: "#2dd4bf" }}
                  />
                </div>
                <span
                  className="text-[14px] font-semibold tracking-[0.02em]"
                  style={{ color: "#29d3b3" }}
                >
                  仅限邀请
                </span>
              </div>
              <button
                type="button"
                aria-label="关闭邀请码弹窗"
                className="flex h-8 w-8 items-center justify-center rounded-full transition-opacity hover:opacity-80"
                style={{ color: "rgba(207,250,245,0.72)" }}
                onClick={() => showInfo("请输入邀请码后加入")}
              >
                <X size={17} />
              </button>
            </div>

            <div className="mb-8 mt-1 flex justify-center">
              <div
                className="relative flex h-[132px] w-[132px] items-center justify-center rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(19,214,177,0.2) 0%, rgba(12,46,42,0.55) 48%, rgba(8,18,22,0) 76%)",
                }}
              >
                <div
                  className="absolute inset-[14px] rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(19,214,177,0.2) 0%, rgba(7,33,33,0.12) 74%, rgba(7,33,33,0) 100%)",
                  }}
                />
                <div
                  className="relative flex h-[86px] w-[86px] items-center justify-center rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(9,49,44,0.92) 0%, rgba(7,37,34,0.98) 100%)",
                    boxShadow: "0 0 32px rgba(19,214,177,0.14)",
                  }}
                >
                  <Users
                    size={34}
                    strokeWidth={2}
                    style={{ color: "#23d6b6" }}
                  />
                </div>
              </div>
            </div>

            <div className="mb-5 text-center">
              <p
                className="text-[18px] font-semibold leading-none tracking-[0.01em]"
                style={{ color: "#f3faf8" }}
              >
                需要邀请码才能加入
              </p>
              <p
                className="mx-auto mt-3 max-w-[220px] text-[14px] leading-6"
                style={{ color: "#a7b6c7" }}
              >
                请输入您收到的邀请码以完成注册
              </p>
            </div>

            <div className="space-y-4">
              <input
                value={inviteCode}
                onChange={handleInputChange}
                onKeyDown={e => {
                  if (e.key === "Enter") handleConfirm();
                }}
                placeholder="输入邀请码"
                maxLength={6}
                autoFocus
                className="h-[46px] w-full rounded-[10px] border px-4 text-center text-[15px] outline-none transition-colors placeholder:text-center placeholder:text-[#5f6979]"
                style={{
                  background: "#111520",
                  borderColor: "rgba(62,73,90,0.7)",
                  color: "#edf7f5",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
                }}
              />
              <button
                className="h-[46px] w-full rounded-[10px] text-[16px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background:
                    "linear-gradient(180deg, #0e5f53 0%, #0a4f45 100%)",
                  color: "#eafffb",
                  boxShadow: "0 14px 28px rgba(8,79,69,0.34)",
                }}
                disabled={!inviteCode.trim()}
                onClick={handleConfirm}
              >
                加入
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default InviteCodeDialog;
